import { Option } from '~/components/Select';
import { TestTagType } from '~/components/Tag';
import { CANADA_POST, FEDEX } from '~/constants/carriers';
import { currencies, Currency } from '~/constants/currency';
import ProviderAccount from '~/types/ProviderAccount';
import { TranslationFunction } from '~/utils/translate';
import CarrierAccount from './CarrierAccount';

type SurchargeDetails = {
  copy: string;
  shouldTranslate: boolean;
  value: number;
};

export type Surcharges = {
  baseCharge: SurchargeDetails;
  fuelSurcharge?: SurchargeDetails;
  residentialSurcharge?: SurchargeDetails;
  customsSurcharge?: SurchargeDetails;
  taxes?: SurchargeDetails;
  otherSurcharges?: SurchargeDetails;
  currency: Maybe<Currency>;
};

type JSONServiceLevelTerms = {
  base_charge: number;
  fuel_surcharge?: number;
  residential_surcharge?: number;
  customs_surcharge?: number;
  taxes?: number;
  other_surcharges?: number;
  currency: string;
};

export type WebRatesServiceLevel = {
  extended_token: string;
  name: string;
  parent_servicelevel: string;
  terms: string;
  token: string;
};

type ServiceLevelTerms = Surcharges | string;

function getCurrency(jsonServiceLevelTerms: JSONServiceLevelTerms) {
  const parsedCurrency = Object.values(currencies).find(
    (currency) => currency.iso === jsonServiceLevelTerms.currency.toUpperCase(),
  );

  return parsedCurrency || currencies['USD'];
}

function parseFedExServiceLevel(jsonServiceLevelTerms: JSONServiceLevelTerms) {
  const terms: ServiceLevelTerms = {
    baseCharge: {
      copy: 'orders:calculateRates:rates:surcharges:baseCharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.base_charge),
    },
    currency: getCurrency(jsonServiceLevelTerms),
  };
  if (jsonServiceLevelTerms.fuel_surcharge) {
    terms.fuelSurcharge = {
      copy: 'orders:calculateRates:rates:surcharges:fuelSurcharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.fuel_surcharge),
    } as SurchargeDetails;
  }

  if (jsonServiceLevelTerms.residential_surcharge) {
    terms.residentialSurcharge = {
      copy: 'orders:calculateRates:rates:surcharges:residentialSurcharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.residential_surcharge),
    };
  }

  if (jsonServiceLevelTerms.customs_surcharge) {
    terms.customsSurcharge = {
      copy: 'orders:calculateRates:rates:surcharges:customsSurcharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.customs_surcharge),
    };
  }

  if (jsonServiceLevelTerms.taxes) {
    terms.taxes = {
      copy: 'orders:calculateRates:rates:surcharges:taxes',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.taxes),
    };
  }
  if (jsonServiceLevelTerms.other_surcharges) {
    terms.otherSurcharges = {
      copy: 'orders:calculateRates:rates:surcharges:otherSurcharges',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.other_surcharges),
    };
  }

  return terms;
}

function parseCanadaPostServiceLevel(jsonServiceLevelTerms: JSONServiceLevelTerms) {
  const parsedTerms = Object.entries(jsonServiceLevelTerms);
  const terms: ServiceLevelTerms = Object.entries(parsedTerms).reduce(
    (termsObject, [, [name, value]]) => {
      if (name === 'currency') {
        termsObject.currency = getCurrency(jsonServiceLevelTerms);
      } else {
        (termsObject as Record<string, unknown>)[name] = {
          shouldTranslate: false,
          copy: name,
          value: Number(value),
        };
      }
      return termsObject;
    },
    {} as Surcharges,
  );

  return terms as ServiceLevelTerms;
}

function fromJSONServiceLevelTerms(serviceLevelTerms: string, provider: string): ServiceLevelTerms {
  let terms: ServiceLevelTerms = '';
  try {
    const jsonServiceLevelTerms: JSONServiceLevelTerms = JSON.parse(serviceLevelTerms);
    if (provider === FEDEX.name) {
      terms = parseFedExServiceLevel(jsonServiceLevelTerms);
    }

    if (provider === CANADA_POST.name) {
      terms = parseCanadaPostServiceLevel(jsonServiceLevelTerms);
    }
  } catch (e) {
    terms = serviceLevelTerms;
  }

  return terms;
}

type JSONServiceLevel = {
  name: string;
  token: string;
  terms: string;
  parent_servicelevel?: JSONServiceLevel;
  extended_token?: string;
};
type ServiceLevel = {
  name: string;
  token: string;
  terms?: ServiceLevelTerms;
  provider_image_75?: string;
  provider_image_200?: string;
  provider_name?: string;
  is_hidden?: boolean;
  is_used?: boolean;
  account_object_id?: string;
  supports_return_labels?: boolean;
  parent_servicelevel?: ServiceLevel;
  extended_token?: string;
};

type MappedServiceLevelsToken = { [token: string]: ServiceLevel[] };

// Copying from ember.
// For some reason we ignore some service levels
// Just doing this to have feature parity
// Some of this have duplicated tokens as well
// FIXME: later pls

const IGNORED_SERVICE_LEVELS_TOKENS = [
  'hermes_uk_home_delivery',
  'hermes_uk_parcelshop',
  'usps_media_mail',
  'usps_library_mail',
];
const IGNORED_SERVICE_LEVELS_NAMES = ['FC Package', 'FC Mail'];

const ONERATE = 'ONERATE';

const SERVICE_LEVEL_CHEAPEST: ServiceLevel = { name: 'Cheapest Rate', token: 'cheapest' };
const SERVICE_LEVEL_FASTEST: ServiceLevel = { name: 'Fastest Rate', token: 'fastest' };

const ServiceLevelCategories: ServiceLevel[] = [SERVICE_LEVEL_CHEAPEST, SERVICE_LEVEL_FASTEST];
const ServiceLevelCategoryByToken: HashMap<string, ServiceLevel> = ServiceLevelCategories.reduce(
  (map, category) => Object.assign(map, { [category.token]: category }),
  {},
);
const ServiceLevelCategoryOptions: Option[] = toOptions(ServiceLevelCategories, false);

function toOptions(serviceLevels: ServiceLevel[], isReturn: boolean): Option<string>[] {
  const filteredServiceLevels = isReturn
    ? serviceLevels
    : serviceLevels.filter((serviceLevel) => {
        return (
          !IGNORED_SERVICE_LEVELS_TOKENS.includes(serviceLevel.token) &&
          !IGNORED_SERVICE_LEVELS_NAMES.includes(serviceLevel.name)
        );
      });

  return filteredServiceLevels.map((serviceLevel) => {
    return {
      value: `${serviceLevel.token}`, // token could be repeated between services levels :/
      label: serviceLevel.name,
    };
  });
}

function isOneRate(serviceLevel: ServiceLevel): boolean {
  return !!serviceLevel && !!serviceLevel.terms && serviceLevel.terms === ONERATE;
}

export const fromWebRatesServiceLevel = (
  webRatesServiceLevel: WebRatesServiceLevel,
): ServiceLevel => {
  return {
    name: webRatesServiceLevel.name,
    token: webRatesServiceLevel.token,
    terms: webRatesServiceLevel.terms || '',
    extended_token: webRatesServiceLevel.extended_token,
  };
};

const addServiceLevelAccountType = (providerAccount: ProviderAccount, t: TranslationFunction) => {
  let providerDescription = '';
  if (providerAccount.isShippoAccount) {
    providerDescription = `(${t('automations.form.shippoMasterAccount')})`;
  } else if (providerAccount.nickname) {
    providerDescription = `(${providerAccount.nickname})`;
  }

  if (providerAccount.isTest) {
    if (providerDescription.length > 0) {
      providerDescription += ' ';
    }
    providerDescription += `- ${t('automations.form.testAccount')}`;
  }
  return providerDescription;
};

const formatOptions = ({
  options,
  defaultServiceLevel,
  t,
}: {
  options: ProviderAccount[];
  defaultServiceLevel: Maybe<string>;
  t: TranslationFunction;
}) => {
  return options
    .sort((a: ProviderAccount, b: ProviderAccount) => {
      if (!a.providerName || !b.providerName) {
        return 0;
      }
      return a.providerName.localeCompare(b.providerName);
    })
    .map((providerAccount: ProviderAccount) => {
      const { visibleServiceLevels } = providerAccount;
      const serviceLevelOptions = toOptions(visibleServiceLevels, false).map(
        ({ label, value }) => ({
          value: providerAccount.objectId + '|' + value,
          label:
            label.toString() +
            (value === defaultServiceLevel
              ? ` (${t('automations.form.defaultServiceLevelOption')})`
              : ''),
        }),
      );

      const providerDescription = addServiceLevelAccountType(providerAccount, t);

      return {
        label: `${providerAccount.providerName} ${providerDescription}`,
        options: serviceLevelOptions,
      };
    });
};

// get Service Level Account by token and accountId, if !accountId, get the first account that return from matched token.
const getServiceLevelAccountByToken = ({
  mappedServiceLevels,
  accountId,
  token,
}: {
  mappedServiceLevels: MappedServiceLevelsToken;
  accountId: Maybe<string>;
  token?: string;
}): ServiceLevel | undefined => {
  if (Object.keys(mappedServiceLevels).length > 0 && token) {
    const serviceLevelAccounts = mappedServiceLevels?.[token];
    if (serviceLevelAccounts?.length > 0) {
      // Check if a Service Level exists with both the token and specified Account
      if (accountId) {
        const serviceLevelWithMatchingAccount = serviceLevelAccounts.find(
          (serviceLevel) => serviceLevel.account_object_id === accountId,
        );
        if (serviceLevelWithMatchingAccount) {
          return serviceLevelWithMatchingAccount;
        }
      }
      // otherwise, simply return the first Account with a matching token
      return serviceLevelAccounts[0];
    }
  }
  return undefined;
};

const serviceLevelMatchesToken = (
  serviceLevel: ServiceLevel,
  serviceLevelToken: string,
): boolean => {
  if (!serviceLevel || !serviceLevelToken) {
    return false;
  }
  // check if an extended token is available for more precise comparisons
  else if (serviceLevel.extended_token && serviceLevel.extended_token === serviceLevelToken) {
    return true;
  }
  // otherwise, check if the tokens match
  else if (serviceLevel.token === serviceLevelToken) {
    return true;
  }
  // finally, if the tokens didn't match, try matching the token of the parent (if a parent exists)
  return (
    !!serviceLevel.parent_servicelevel &&
    serviceLevelMatchesToken(serviceLevel.parent_servicelevel, serviceLevelToken)
  );
};

const getProviderAccountByObjectId = (
  accounts: Maybe<ProviderAccount[]>,
  objectId?: string | number,
) => {
  if (!accounts || accounts.length === 0 || !objectId) return;
  return accounts.find((account) => account.objectId === objectId);
};

export type ServiceLevelData = {
  provider: string; // 'USPS'
  name: string; // 'USPS Priority Mail'
  count: number; // 5
  amount?: number;
  currency?: string;
  estimatedDays?: number; // 7
  carrierAccount?: CarrierAccount; // Carrier Account Object for the selected Rate
  testMode?: TestTagType;
  isTrackable?: boolean; // whether the package is trackable or no
  token: string;
};

export type BatchServiceLevelData = {
  data: {
    [token: string]: ServiceLevelData;
  };
  // orders count
  count: number;
};

export type AvailableServiceLevels = {
  domestic: BatchServiceLevelData;
  international: BatchServiceLevelData;
};

export default ServiceLevel;

export {
  SERVICE_LEVEL_CHEAPEST,
  SERVICE_LEVEL_FASTEST,
  ServiceLevelCategories,
  ServiceLevelCategoryByToken,
  ServiceLevelCategoryOptions,
  SurchargeDetails,
  toOptions,
  isOneRate,
  fromJSONServiceLevelTerms,
  formatOptions,
  addServiceLevelAccountType,
  getServiceLevelAccountByToken,
  getProviderAccountByObjectId,
  serviceLevelMatchesToken,
  JSONServiceLevel,
  ServiceLevelTerms,
  MappedServiceLevelsToken,
};
